import { useEffect, useMemo, useRef, useState } from "react";
import "./Paywall.scss";
import {
  faCalendarCheck,
  faCheck,
  faComments,
  faCrown,
  faHandHoldingDollar,
  faUsersViewfinder,
  faWandMagicSparkles,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getSubscriptionCheckoutSession } from "services/SauceService";
import { useDispatch, useSelector } from "react-redux";
import { BusinessState, getBusinessSubscription } from "redux-context/business";
import { SubscriptionType } from "enums/SubscriptionType";
import { UserState } from "redux-context/user";
import { useLocation } from "react-router-dom";
import {
  trackCheckoutPageViewed,
  trackCheckoutSuccess,
  trackUpgradeModalSubscribeClicked,
  trackUpgradeModalViewed,
} from "analytics";
import { isMobile } from "react-device-detect";
import PartnerPaywallContent from "./PartnerPaywallContent";
import { getPartnerExperience } from "partners/partnerExperiences";

export enum PaywallSource {
  Creators = "creators",
  CreatorInvite = "creator-invite",
  LaunchCampaign = "launch-campaign",
  LaunchCollab = "launch-collab",
  SendMessage = "send-message",
  BookingActions = "booking-actions",
  UpgradePlan = "upgrade-plan",
}

export type PaywallOptions = {
  source?: PaywallSource;
  onSubscribe?: () => void;
  priceId?: string;
};

type Props = {
  open: boolean;
  onRequestClose: () => void;
  options?: PaywallOptions;
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const labelsBySource: Record<string, string> = {
  [PaywallSource.Creators]: "Upgrade to see all creators",
  [PaywallSource.CreatorInvite]: "Subscribe & Send Invite",
  [PaywallSource.LaunchCampaign]: "Subscribe & Launch Campaign",
  [PaywallSource.LaunchCollab]: "Subscribe & Launch Collab",
  [PaywallSource.SendMessage]: "Subscribe & Send Message",
  [PaywallSource.BookingActions]: "Subscribe & Continue",
  [PaywallSource.UpgradePlan]: "Upgrade Plan",
};

const Paywall: React.FC<Props> = ({ open, onRequestClose, options }) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>();
  const { subscription, business } = useSelector(
    (state: BusinessState) => state.business,
  );
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;
  const partnerExperience = useMemo(
    () => getPartnerExperience(subscription?.partner),
    [subscription?.partner],
  );
  const partnerPaywallVariant = partnerExperience?.paywallVariant;

  const [paymentComplete, setPaymentComplete] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const pollIntervalRef = useRef<number | null>(null);
  const hasTrackedUpgradeModalViewedRef = useRef(false);
  const hasTrackedCheckoutPageViewedRef = useRef(false);
  const isPartnerVariant = Boolean(
    partnerPaywallVariant && !clientSecret && !paymentComplete,
  );

  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId: business?._id ?? null,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType: subscription?.planName ?? null,
      currency:
        subscription?.stripeDetails?.nextPaymentDetails?.currency ?? null,
    }),
    [
      business?._id,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      subscription?.planName,
      subscription?.stripeDetails?.nextPaymentDetails?.currency,
    ],
  );

  const onCompletePayment = useMemo(() => {
    return () => {
      trackCheckoutSuccess(analyticsContext);

      // immediately refresh subscription
      dispatch(getBusinessSubscription());

      // clear any existing interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // start polling every 2 seconds
      pollIntervalRef.current = window.setInterval(() => {
        dispatch(getBusinessSubscription());
      }, 2000);
      setIsPolling(true);
    };
  }, [analyticsContext, dispatch]);

  useEffect(() => {
    // clear polling when modal is closed
    if (!open) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setPaymentComplete(false);
      setIsPolling(false);
      hasTrackedUpgradeModalViewedRef.current = false;
      hasTrackedCheckoutPageViewedRef.current = false;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [open]);

  useEffect(() => {
    if (!open || Boolean(clientSecret)) {
      return;
    }

    if (hasTrackedUpgradeModalViewedRef.current) {
      return;
    }

    hasTrackedUpgradeModalViewedRef.current = true;
    trackUpgradeModalViewed(analyticsContext);
  }, [analyticsContext, clientSecret, open]);

  useEffect(() => {
    if (!open || !clientSecret) {
      if (!clientSecret) {
        hasTrackedCheckoutPageViewedRef.current = false;
      }
      return;
    }

    if (hasTrackedCheckoutPageViewedRef.current) {
      return;
    }

    hasTrackedCheckoutPageViewedRef.current = true;
    trackCheckoutPageViewed(analyticsContext);
  }, [analyticsContext, clientSecret, open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onRequestClose();
    }

    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onRequestClose]);

  function onBackdropClick(e: React.MouseEvent) {
    if (e.target === modalRef.current) onRequestClose();
  }

  const onSubscribeClick = async () => {
    if (loading) return;
    trackUpgradeModalSubscribeClicked(analyticsContext);
    setLoading(true);
    const params = {
      businessId: business?._id,
    };
    try {
      const resp: any = await getSubscriptionCheckoutSession(params);

      const secret = resp?.sessionId;
      if (!secret)
        throw new Error("Missing client secret from server response");

      setClientSecret(secret);
      // clear previous interval if any
      // if ((window as any)._paywallSubscriptionInterval) {
      //   clearInterval((window as any)._paywallSubscriptionInterval);
      //   (window as any)._paywallSubscriptionInterval = null;
      // }

      // (window as any)._paywallSubscriptionInterval = window.setInterval(() => {
      //   dispatch(getBusinessSubscription());
      // }, 15000);
    } catch (err: any) {
      console.error(err);
      window.alert(
        err?.message || "Unable to start checkout. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!freemium) {
      options?.onSubscribe?.();
      onRequestClose();
    }
  }, [freemium, onRequestClose, options]);

  return (
    <>
      <div
        ref={modalRef}
        className={`pw-backdrop ${open ? "pw-visible" : ""}`}
        onClick={onBackdropClick}
        aria-hidden={!open}
      >
        <div
          id="modalContent"
          className={`pw-modal ${open ? "pw-open" : ""} ${
            clientSecret ? "pw-modal--embedded" : ""
          } ${
            isPartnerVariant && partnerPaywallVariant
              ? `pw-modal--${partnerPaywallVariant}`
              : ""
          }`}
          role="dialog"
          aria-modal={open}
          aria-label="Paywall modal"
        >
          {paymentComplete ? (
            <div className="pw-success">
              <div className="pw-success-icon" aria-hidden>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <h2>Payment Successful!</h2>
              <p>Thank you for your subscription.</p>
              {isPolling && <div className="pw-spinner" aria-hidden />}
              <button className="pw-cta" type="button" onClick={onRequestClose}>
                Close
              </button>
            </div>
          ) : clientSecret ? (
            <div style={{ width: "100%", height: "100%", overflow: "scroll" }}>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret,
                  onComplete: onCompletePayment,
                }}
              >
                <div className="pw-embedded">
                  <button
                    className="pw-close"
                    onClick={() => {
                      // unmount embedded checkout if user closes modal
                      setClientSecret(null);
                      onRequestClose();
                    }}
                    aria-label="Close"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                  </button>
                  <EmbeddedCheckout />
                </div>
              </EmbeddedCheckoutProvider>
            </div>
          ) : isPartnerVariant && partnerPaywallVariant ? (
            <PartnerPaywallContent
              variant={partnerPaywallVariant}
              loading={loading}
              onCtaClick={onSubscribeClick}
              ctaLabel="Start My Free Campaign"
              onClose={onRequestClose}
              showCloseButton
              desktopTrustedByText="Trusted by 1000+ local restaurants"
              mobileTrustedByText="Trusted by 1000+ local businesses"
              showTerms
            />
          ) : (
            <>
              <div className="pw-left">
                <div className="pw-decor pw-decor--orange" />
                <div className="pw-decor pw-decor--blue" />

                <div className="pw-left-inner">
                  <div className="pw-badge">
                    <FontAwesomeIcon icon={faCrown} />
                    <span>Recommended Plan</span>
                  </div>

                  <h2 className="pw-title">Talk of the Town Plan</h2>
                  <p className="pw-sub">Self-Serve Checkout Plan</p>
                  <p className="pw-note"></p>

                  <div className="pw-price">
                    <span className="pw-amount">$299</span>
                    <span className="pw-period">/month</span>
                  </div>

                  <div className="pw-features">
                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        3 interested local influencers
                      </span>
                    </div>

                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        Unlimited locations
                      </span>
                    </div>

                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        3 posts + 3 stories
                      </span>
                    </div>

                    <div className="pw-feature">
                      <span className="pw-dot">
                        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                      </span>
                      <span className="pw-feature-text">
                        10K+ local followers
                      </span>
                    </div>
                  </div>

                  <button
                    className={`pw-cta ${loading ? "pw-cta--loading" : ""}`}
                    type="button"
                    onClick={onSubscribeClick}
                    disabled={loading}
                    aria-busy={loading}
                  >
                    {loading
                      ? "Processing…"
                      : labelsBySource[options?.source || ""] ||
                        "Subscribe Now"}
                  </button>

                  <p className="pw-small">
                    Cancel anytime, no commitment until applications are
                    delivered.
                  </p>
                  <p className="pw-terms">
                    By subscribing, you agree to{" "}
                    <a href="https://www.mustard.love/terms-of-service">
                      Mustard&apos;s Terms of Service
                    </a>
                  </p>
                </div>
              </div>

              <div className={`pw-right ${isMobile ? "mobile" : ""}`}>
                <button
                  className="pw-close"
                  onClick={onRequestClose}
                  aria-label="Close"
                  type="button"
                >
                  <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>

                <h3 className="pw-right-title">Unlock Full Access</h3>
                <p className="pw-right-sub">
                  Upgrade to the Talk of the Town plan and get everything you
                  need to scale your influencer marketing.
                </p>

                <div className="pw-right-scroll">
                  <h4 className="pw-list-title">
                    Everything included in this plan
                  </h4>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--orange">
                      <FontAwesomeIcon
                        icon={faUsersViewfinder}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Guaranteed Applications</h5>
                      <p>
                        Invite creators and get guaranteed 3+ applications each
                        month.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--blue">
                      <FontAwesomeIcon
                        icon={faWandMagicSparkles}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>AI + Manual Matching</h5>
                      <p>
                        Use AI to match creators or handpick from applications —
                        your choice.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--purple">
                      <FontAwesomeIcon
                        icon={faCalendarCheck}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Easy Scheduling</h5>
                      <p>
                        Easily schedule creators to visit as often as you want.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--green">
                      <FontAwesomeIcon
                        icon={faHandHoldingDollar}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="pw-item-body">
                      <h5>Flexible Compensation</h5>
                      <p>
                        Provide flexible compensation options, including food,
                        cash, or affiliate commissions.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--pink">
                      <FontAwesomeIcon icon={faComments} aria-hidden="true" />
                    </div>
                    <div className="pw-item-body">
                      <h5>Direct Access</h5>
                      <p>
                        Gain access to the largest local creator pool, complete
                        with performance insights and direct chat.
                      </p>
                    </div>
                  </div>

                  <div className="pw-item">
                    <div className="pw-icon pw-icon--orange">
                      <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                    </div>
                    <div className="pw-item-body">
                      <h5>Cancel Anytime</h5>
                      <p>
                        Cancel anytime, no commitment until applications are
                        delivered.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Paywall;
